import React, { useState, useEffect, useRef } from "react";
import { 
  PackageIcon, 
  Upload, 
  RefreshCw, 
  Search, 
  CheckCircle2, 
  AlertTriangle, 
  X,
  FileUp,
  Cpu,
  History,
  Play,
  Smartphone
} from "lucide-react";
import axios from "../api/axios";

const FirmwareCommands = () => {
  const [firmwares, setFirmwares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  
  // Upgrade state
  const [upgradeData, setUpgradeData] = useState({ imei: "", version: "" });

  const fileInputRef = useRef(null);

  const fetchFirmwares = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/firmware/list");
      setFirmwares(response.data || []);
    } catch (err) {
      setError("Không thể tải danh sách firmware");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFirmwares();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSubmitting(true);
    setError(null);
    const form = new FormData();
    form.append("file", file);

    try {
      await axios.post("/firmware/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccessMsg("Tải lên firmware thành công!");
      fetchFirmwares();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError("Lỗi khi tải lên firmware");
    } finally {
      setSubmitting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleUpgrade = async (e) => {
    e.preventDefault();
    if (!upgradeData.imei || !upgradeData.version) {
      setError("Vui lòng nhập IMEI và chọn phiên bản firmware");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await axios.post("/firmware/upgrade", upgradeData);
      setSuccessMsg(`Đã kích hoạt nâng cấp cho thiết bị ${upgradeData.imei}`);
      setUpgradeData({ ...upgradeData, imei: "" });
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Lỗi khi kích hoạt nâng cấp");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "---";
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 p-4 md:p-6 font-sans">
      <div className="max-w-[98%] mx-auto">
        
        {/* Header Section */}
        <div className="mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-3">
              <Cpu className="text-blue-600" size={28} />
              Quản Lý Firmware (OTA)
            </h1>
            <p className="text-gray-500 text-sm font-medium italic tracking-tight">Cập nhật phần mềm từ xa cho thiết bị</p>
          </div>
          
          <div className="flex gap-3">
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleUpload} 
              className="hidden" 
            />
            <button 
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-2xl font-bold shadow-xl shadow-blue-100 transition-all active:scale-95 disabled:opacity-50"
            >
              <Upload size={20} /> TẢI LÊN FIRMWARE
            </button>
            <button 
              onClick={fetchFirmwares}
              className="p-3 bg-gray-100 text-gray-600 rounded-2xl hover:bg-gray-200 transition-all"
            >
              <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Global Messages */}
        {successMsg && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex justify-between items-center text-emerald-700 font-bold animate-in fade-in slide-in-from-top-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={20} />
              <span>{successMsg}</span>
            </div>
            <button onClick={() => setSuccessMsg(null)}><X size={18}/></button>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-2xl flex justify-between items-center text-rose-600 font-bold animate-in fade-in slide-in-from-top-4">
            <div className="flex items-center gap-3">
              <AlertTriangle size={20} />
              <span>{error}</span>
            </div>
            <button onClick={() => setError(null)}><X size={18}/></button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upgrade Form */}
          <div className="lg:col-span-1">
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 sticky top-6">
              <h2 className="text-lg font-black text-gray-900 mb-6 flex items-center gap-2">
                <Play size={20} className="text-blue-600" />
                KÍCH HOẠT NÂNG CẤP
              </h2>
              
              <form onSubmit={handleUpgrade} className="space-y-6">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Số IMEI</label>
                  <div className="relative">
                    <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={20} />
                    <input 
                      type="text" 
                      value={upgradeData.imei}
                      onChange={(e) => setUpgradeData({...upgradeData, imei: e.target.value})}
                      className="w-full pl-12 pr-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-mono font-bold text-gray-700" 
                      placeholder="Nhập IMEI..." 
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Phiên bản Firmware</label>
                  <div className="relative">
                    <PackageIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={20} />
                    <select 
                      value={upgradeData.version}
                      onChange={(e) => setUpgradeData({...upgradeData, version: e.target.value})}
                      className="w-full pl-12 pr-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-700 appearance-none"
                    >
                      <option value="">Chọn phiên bản...</option>
                      {firmwares.map((fw) => (
                        <option key={fw.version} value={fw.version}>
                          {fw.version} ({fw.filename})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <button 
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 bg-emerald-600 text-white font-black rounded-2xl shadow-xl shadow-emerald-100 hover:bg-emerald-700 transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {submitting ? (
                    <RefreshCw size={20} className="animate-spin" />
                  ) : (
                    <Play size={20} />
                  )}
                  BẮT ĐẦU NÂNG CẤP
                </button>
              </form>
            </div>
          </div>

          {/* Firmware List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-black text-gray-900 flex items-center gap-2">
                  <History size={20} className="text-blue-600" />
                  DANH SÁCH FIRMWARE
                </h2>
              </div>

              <div className="overflow-x-auto">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <RefreshCw size={40} className="animate-spin text-blue-500 mb-4 opacity-50" />
                    <p className="font-black text-gray-400 uppercase text-xs tracking-widest">Đang tải dữ liệu...</p>
                  </div>
                ) : firmwares.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                    <PackageIcon size={48} className="mb-4 opacity-20" />
                    <p className="font-bold text-sm">Chưa có bản firmware nào được tải lên</p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead className="bg-gray-50/50">
                      <tr>
                        <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Phiên bản</th>
                        <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Tên tệp</th>
                        <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Ngày tải lên</th>
                        <th className="px-6 py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Kích thước</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-50">
                      {firmwares.map((item, idx) => (
                        <tr key={item.version || idx} className="hover:bg-blue-50/20 transition-all group">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-blue-50 text-blue-600 rounded-lg font-black text-xs">
                                {item.version}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm font-bold text-gray-900">{item.filename}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-xs font-medium text-gray-500">{formatDate(item.created_at)}</span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <span className="text-xs text-gray-400 font-bold">
                              {item.size ? `${(item.size / 1024).toFixed(2)} KB` : "---"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirmwareCommands;
